import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, Flame, TrendingUp, Coffee, Rocket } from "lucide-react";
import Navbar from "../components/Navbar";
import SignalBadge from "../components/SignalBadge";
import TierBadge from "../components/TierBadge";
import AlertClassBadge from "../components/AlertClassBadge";
import ScorePill from "../components/ScorePill";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api } from "../lib/api";

const TIER_OPTIONS = [
  { id: "small,mid", label: "Breakouts", desc: "Small + Mid cap (Moonbucks edge)", Icon: Flame },
  { id: "small", label: "Small only", desc: "Highest upside, highest risk", Icon: Flame },
  { id: "small,mid,large", label: "+ Large", desc: "Add liquid large caps", Icon: TrendingUp },
  { id: "small,mid,large,mega", label: "All", desc: "Include mega-caps", Icon: TrendingUp },
];

const CLASS_FILTERS = [
  { id: "all", label: "All Alerts" },
  { id: "moonshot", label: "Moonshot only", Icon: Rocket },
  { id: "steady", label: "Steady only", Icon: Coffee },
];

export default function Screener() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState("small,mid");
  const [classFilter, setClassFilter] = useState("all");

  const load = async (tiers = tierFilter) => {
    setLoading(true);
    try {
      const r = await api.get(`/screener/run?tiers=${tiers}`);
      setData(r.data);
    } catch (e) {
      console.error("screener load failed", e);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredBuy = useMemo(() => {
    const buy = data?.buy_candidates ?? [];
    if (classFilter === "all") return buy;
    return buy.filter((c) => (c.alert_class || "steady") === classFilter);
  }, [data, classFilter]);

  const filteredWatch = useMemo(() => {
    const w = data?.watch_no_trade ?? [];
    if (classFilter === "all") return w;
    return w.filter((c) => (c.alert_class || "steady") === classFilter);
  }, [data, classFilter]);

  const classCounts = data?.class_counts || {};
  const moonCount = classCounts.moonshot || 0;
  const steadyCount = classCounts.steady || 0;

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-6">
          <div>
            <div className="label-uppercase text-mb-emerald">Pressure Screener</div>
            <h1 className="font-display font-semibold text-4xl tracking-tight mt-1">Stocks building up</h1>
            <p className="text-neutral-600 mt-1 text-sm">
              {data ? `Scanned ${data.universe_size} tickers · ${new Date(data.scanned_at).toLocaleTimeString()}` : "Scanning…"}
            </p>
          </div>
          <Button variant="outline" onClick={() => load()} className="rounded-md" data-testid="screener-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* TIER FILTER */}
        <div className="bg-white border border-mb-border rounded-lg p-4 mb-6" data-testid="tier-filter-bar">
          <div className="flex items-center justify-between mb-3">
            <div className="label-uppercase text-neutral-500">Universe focus</div>
            {data?.tier_counts && (
              <div className="font-mono-data text-xs text-neutral-500 hidden sm:flex gap-3">
                {Object.entries(data.tier_counts).map(([t, n]) => (
                  <span key={t}><b className="text-mb-emerald">{n}</b> {t}</span>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {TIER_OPTIONS.map((opt) => {
              const active = tierFilter === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => { setTierFilter(opt.id); load(opt.id); }}
                  className={`group flex items-start gap-3 text-left p-3 rounded-xl border transition ${active ? "bg-mb-emerald text-white border-mb-emerald" : "bg-white border-mb-border hover:border-mb-emerald/40"}`}
                  data-testid={`tier-pill-${opt.id.replace(/,/g, "-")}`}
                >
                  <opt.Icon className={`w-4 h-4 mt-0.5 ${active ? "text-white" : "text-mb-emerald"}`} />
                  <div>
                    <div className="font-display font-bold text-sm">{opt.label}</div>
                    <div className={`text-[11px] mt-0.5 ${active ? "text-white/70" : "text-neutral-500"}`}>{opt.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-neutral-500">
            <Flame className="w-3 h-3 inline-block mr-1 text-amber-500" />
            <b>Why small + mid?</b> A $5 stock doubling on news is a 100% gain. A $300 mega-cap moving $5 is 1.6%. Moonbucks finds the early movers.
          </p>
        </div>

        {/* DUAL-TIER ALERT CLASS EXPLAINER + FILTER */}
        <div className="grid md:grid-cols-2 gap-3 mb-6" data-testid="alert-classes">
          <ClassPanel
            active={classFilter === "moonshot" || classFilter === "all"}
            primary={classFilter === "moonshot"}
            onClick={() => setClassFilter(classFilter === "moonshot" ? "all" : "moonshot")}
            kind="moonshot"
            count={moonCount}
            title="🚀 Moonshot"
            tagline="Pre-Breakout Index — catches energy BEFORE it shows in price."
            stat="+13% best · +7% avg win · 6 moonshots at +20%"
            note="2 of 3 leading signals: Coiled Spring · Stealth Loading · Relative Lift"
            testid="class-panel-moonshot"
          />
          <ClassPanel
            active={classFilter === "steady" || classFilter === "all"}
            primary={classFilter === "steady"}
            onClick={() => setClassFilter(classFilter === "steady" ? "all" : "steady")}
            kind="steady"
            count={steadyCount}
            title="☕ Steady"
            tagline="Strict A-Grade — 5 of 6 Pressure Index components must fire."
            stat="85% WR · +2.1% per trade · the cup of coffee insurance"
            note="6 components: Institutional Flow · Pressure Release · Trend Stack · Posture · Ignition · Entry Zone"
            testid="class-panel-steady"
          />
        </div>

        {classFilter !== "all" && (
          <div className="mb-4 text-xs text-neutral-500">
            Filter active: <button onClick={() => setClassFilter("all")} className="text-mb-emerald font-bold underline" data-testid="clear-class-filter">show all alerts</button>
          </div>
        )}

        {loading && <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-mb-emerald" /></div>}

        {data && (
          <Tabs defaultValue="buy" className="w-full" data-testid="screener-tabs">
            <TabsList className="bg-white border border-mb-border p-1">
              <TabsTrigger value="buy" data-testid="tab-buy" className="data-[state=active]:bg-mb-emerald data-[state=active]:text-white rounded-md">
                BUY ({filteredBuy.length})
              </TabsTrigger>
              <TabsTrigger value="watch" data-testid="tab-watch">
                Watch / No-Trade ({filteredWatch.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="buy" className="mt-6">
              {filteredBuy.length === 0
                ? <Empty msg={classFilter === "all" ? "No BUY candidates passing either tier right now. Try a different universe filter or check back in 15 min." : `No ${classFilter} BUY candidates right now. The other tier may have alerts — try clearing the filter.`} />
                : <Grid items={filteredBuy} />}
            </TabsContent>
            <TabsContent value="watch" className="mt-6">
              {filteredWatch.length === 0
                ? <Empty msg="No watchlist candidates either — markets are quiet for this universe." />
                : <Grid items={filteredWatch} />}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function Grid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="screener-grid">
      {items.map((it) => <Card key={it.symbol} item={it} />)}
    </div>
  );
}

function Card({ item }) {
  const ind = item.indicators;
  const cls = item.alert_class || "steady";
  const isMoon = cls === "moonshot";
  const score = item.screen?.score ?? 0;
  const maxScore = item.screen?.max_score ?? (isMoon ? 3 : 6);
  return (
    <Link
      to={`/app/stock/${item.symbol}`}
      className={`block bg-white border-2 rounded-lg p-5 hover:-translate-y-0.5 transition ${isMoon ? "border-pink-200 hover:border-pink-400" : "border-mb-border hover:border-mb-emerald/40"}`}
      data-testid={`screener-card-${item.symbol}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-mono-data font-extrabold text-2xl">{item.symbol}</div>
            <AlertClassBadge alertClass={cls} compact />
            <ScorePill grade={item.moonbucks_grade} size="xs" />
          </div>
          <div className="font-mono-data text-xs text-neutral-500 mt-0.5">
            ${ind.last_price}
            {typeof ind.change_today_pct === "number" && (
              <span className={`ml-1 ${ind.change_today_pct >= 0 ? "text-buy" : "text-sell"}`}>
                {ind.change_today_pct >= 0 ? "+" : ""}{ind.change_today_pct}% today
              </span>
            )}
            <span className="text-neutral-400"> · </span>
            <span className={ind.momentum_5d >= 0 ? "text-buy" : "text-sell"}>
              {ind.momentum_5d >= 0 ? "+" : ""}{ind.momentum_5d}% (5d)
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <SignalBadge action={item.decision.action} confidence={item.decision.confidence} />
          <TierBadge tier={item.tier} upside={item.upside} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Stat label="Posture" val={ind.rsi >= 55 && ind.rsi <= 68 ? "Optimal" : (ind.rsi > 68 ? "Hot" : "Cool")} highlight={ind.rsi >= 55 && ind.rsi <= 68} />
        <Stat label="Flow" val={ind.volume_ratio >= 2.5 ? "Heavy" : (ind.volume_ratio >= 1.7 ? "Strong" : "Light")} highlight={ind.volume_ratio >= 1.7} />
        <Stat label={isMoon ? "PBI" : "Index"} val={`${score}/${maxScore}`} highlight={isMoon ? score >= 2 : score >= 5} />
      </div>

      <div className="mt-4 flex flex-wrap gap-1" data-testid="pressure-components">
        {item.screen.signals.map((s) => (
          <span
            key={s.name}
            className={`px-2 py-0.5 text-[10px] rounded-md font-mono-data tracking-wide ${
              s.fired
                ? (isMoon ? "bg-pink-100 text-pink-800 font-bold" : "bg-mb-emerald/10 text-mb-emerald font-bold")
                : "bg-neutral-100 text-neutral-400 line-through"
            }`}
            title={s.fired ? `${s.name} — ${s.detail}` : `${s.name} not active`}
          >
            {s.name}
          </span>
        ))}
      </div>
    </Link>
  );
}

function ClassPanel({ active, primary, onClick, kind, count, title, tagline, stat, note, testid }) {
  const isMoon = kind === "moonshot";
  const baseStyle = isMoon
    ? "bg-gradient-to-br from-pink-500 to-pink-700 text-white"
    : "bg-mb-emerald text-white";
  const inactive = "bg-white border-mb-border text-neutral-700";
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg p-5 border-2 transition ${primary ? `${baseStyle} border-transparent` : `${inactive} ${active ? "" : "opacity-60"}`}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className={`font-display font-semibold text-xl tracking-tight`}>{title}</div>
          <p className={`text-sm mt-1 ${primary ? "text-white/85" : "text-neutral-600"}`}>{tagline}</p>
        </div>
        <div className={`font-mono-data font-extrabold text-3xl ${primary ? "text-white" : (isMoon ? "text-pink-600" : "text-mb-emerald")}`}>
          {count}
        </div>
      </div>
      <div className={`mt-3 text-xs font-mono-data ${primary ? "text-white/80" : "text-neutral-700"}`}>{stat}</div>
      <div className={`mt-2 text-[10px] font-mono-data tracking-wide ${primary ? "text-white/60" : "text-neutral-500"}`}>{note}</div>
    </button>
  );
}

function Stat({ label, val, highlight }) {
  return (
    <div className={`p-2 rounded-lg ${highlight ? "bg-mb-emerald/10" : "bg-neutral-50"}`}>
      <div className={`font-mono-data font-bold ${highlight ? "text-mb-emerald" : "text-neutral-700"}`}>{val}</div>
      <div className="label-uppercase text-[9px] text-neutral-500">{label}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="py-16 text-center text-neutral-500 bg-white rounded-lg border border-mb-border">{msg}</div>;
}
